import React from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import GuideSection from './GuideSection';
import BasicButton from '@/components/Common/Button/BasicButton';
import MenuHeader from '@/components/Header/MenuHeader';
import {
  CheckCircleSolidIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  InformationCircleIcon,
  LoanConsultationOutlineIcon,
  LockedShieldColorIcon,
  SaleTruckOutlineIcon,
  SearchTruckOutlineIcon,
} from '@/components/Icon';
import DocumentTextOutlineIcon from '@/components/Icon/DocumentTextOutlineIcon';
import ManagementMenus from '@/components/More/ManagementMenus';
import ManageMentSubMenus from '@/components/More/ManagementSubMenus';
import ProfileImage from '@/components/More/ProfileImage';
import { colors } from '@/const/colors';
import { useMemberContext } from '@/contexts/Member/MemberContext';
import '@/css/tailwind.css';

const MorePageMain: React.FC = () => {
  const { member } = useMemberContext();
  const navigate = useNavigate();

  const isVerified = Number(member?.authentication) === 1;

  return (
    <>
      <div className="w-full">
        <div className="fixed z-50 flex items-center justify-between">
          <MenuHeader title="더보기" isMain={true}></MenuHeader>
        </div>
        <div className="pt-[60px] pb-[80px]">
          <div className="flex flex-col justify-center bg-gray-0 border border-gray-3 p-4 mx-4 rounded-lg h-[88px] shadow-rightBottom">
            {member ? (
              <div className="z-10 flex items-center jusify-between ">
                <div className="flex-1">
                  <div className="flex items-center">
                    <p className="text-gray-9 text-[24px] mr-2 font-semibold leading-[28px]">{member?.name}</p>
                    {isVerified ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full text-[12px] font-semibold"
                        style={{ backgroundColor: colors.primary[1], color: colors.primary[10] }}
                      >
                        <CheckCircleSolidIcon color={colors.primary[10]} width={14} height={14} />
                        인증
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full text-[12px] font-semibold"
                        style={{ backgroundColor: '#FFF3E9', color: '#EA580C' }}
                      >
                        <InformationCircleIcon color="#EA580C" width={14} height={14} />
                        미인증
                      </span>
                    )}
                  </div>
                  <p className="text-gray-7 text-base leading-[20px]">
                    {member?.email ? member.email : member?.phoneNumber}
                  </p>
                </div>
                <ProfileImage isOnlyShow></ProfileImage>
              </div>
            ) : (
              <Link to={'/login'} className="flex items-center">
                <p className="flex-1 m-2 font-semibold text-[18px] text-gray-8">회원가입/로그인 하러가기</p>
                <ChevronRightIcon color={colors.gray[5]}></ChevronRightIcon>
              </Link>
            )}
          </div>
          {member && !isVerified && (
            <div
              className="flex items-center gap-3 mx-4 mt-3 p-4 rounded-lg cursor-pointer"
              style={{ backgroundColor: '#FFF7ED' }}
              onClick={() => navigate('/identity-verification')}
            >
              <LockedShieldColorIcon width={28} height={28} />
              <div className="flex-1">
                <p className="text-[15px] font-semibold" style={{ color: '#9A3412' }}>
                  본인인증이 필요해요
                </p>
                <p className="text-[13px] leading-[18px]" style={{ color: '#C2410C' }}>
                  서비스 이용을 위해 NICE 본인인증을 완료해 주세요
                </p>
              </div>
              <span
                className="flex items-center text-[13px] font-semibold whitespace-nowrap"
                style={{ color: '#EA580C' }}
              >
                인증하기
                <ChevronRightIcon color="#EA580C" width={16} height={16} />
              </span>
            </div>
          )}
          {member && (
            <div className="flex w-full px-4 mt-[10px]">
              <BasicButton
                name="설정"
                icon={<Cog6ToothIcon color={colors.gray[9]}></Cog6ToothIcon>}
                bgColor={colors.gray[1]}
                borderColor={colors.gray[3]}
                textColor={colors.gray[10]}
                fontSize={16}
                width="100%"
                height={48}
                fontWeight="semibold"
                onClick={() => navigate('/setting')}
              />
            </div>
          )}
          <hr className="mt-6 mb-3 border-4 border-gray-1"></hr>
          {member && (
            <>
              <ManagementMenus></ManagementMenus>
              <hr className="border-4 border-gray-1"></hr>
              {/* <hr className="mx-4 my-3" /> */}
            </>
          )}
          {/* <ManageMentSubMenus></ManageMentSubMenus> */}

          <div className="px-4">
            <ul>
              <li className="list-none">
                <p className="pt-4 pb-2 font-semibold text-gray-8">부가 서비스</p>
                <Link to={'/one-stop-service'} className="flex items-center py-5 cursor-pointer">
                  <SaleTruckOutlineIcon color={colors.gray[7]} width={28} height={28} />
                  <p className="flex-1 ml-4 text-lg font-semibold text-gray-8">알선판매 서비스</p>
                  <ChevronRightIcon color={colors.gray[5]}></ChevronRightIcon>
                </Link>
                <Link to={'/purchase-accompanying-service'} className="flex items-center py-5 cursor-pointer">
                  <SearchTruckOutlineIcon color={colors.gray[7]} width={28} height={28} />
                  <p className="flex-1 ml-4 text-lg font-semibold text-gray-8">구매동행 서비스</p>
                  <ChevronRightIcon color={colors.gray[5]}></ChevronRightIcon>
                </Link>
                <Link to={'/transfer-agency-service'} className="flex items-center py-5 cursor-pointer">
                  <DocumentTextOutlineIcon color={colors.gray[7]} width={28} height={28} />
                  <p className="flex-1 ml-4 text-lg font-semibold text-gray-8">이전대행 서비스</p>
                  <ChevronRightIcon color={colors.gray[5]}></ChevronRightIcon>
                </Link>
                <Link to={'/capital-counsel-service'} className="flex items-center py-5 cursor-pointer">
                  <LoanConsultationOutlineIcon color={colors.gray[7]} width={28} height={28} />
                  <p className="flex-1 ml-4 text-lg font-semibold text-gray-8">대출상담 서비스</p>
                  <ChevronRightIcon color={colors.gray[5]}></ChevronRightIcon>
                </Link>
              </li>
            </ul>
          </div>
          <hr className="my-3 border-4 border-gray-1"></hr>
          <GuideSection></GuideSection>

          <hr className="my-3 border-4 border-gray-1"></hr>
          <ul className="px-4">
            <li className="py-5 list-none">
              <Link to="/notice" className="flex items-center cursor-pointer">
                <p className="flex-1 font-semibold text-gray-8">공지사항</p>
                <ChevronRightIcon color={colors.gray[5]}></ChevronRightIcon>
              </Link>
            </li>
            <li className="py-5 list-none">
              <Link to="/terms" className="flex items-center cursor-pointer">
                <p className="flex-1 font-semibold text-gray-8">서비스 이용약관 확인</p>
                <ChevronRightIcon color={colors.gray[5]}></ChevronRightIcon>
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default MorePageMain;
